import { useCallback, useState } from "react";
import api from "../../axios/axiosInstance";

const useBatchAssessmentSave = ({
    subjectId,
    unitId,
    termNumber,
    skills = [],
    dokLevels = [],
    students = [],
    studentState = {},
    onSaveSuccess,
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const baseUrl = process.env.REACT_APP_BACKEND_URL;

    // =========================================================
    // Validate one student before building payload
    // =========================================================

    const validateStudent = useCallback(
        (studentId, student) => {
            const studentName =
                students.find(
                    (row) =>
                        row.student === studentId
                )?.student_full_name ||
                `Student ${studentId}`;

            if (!student) {
                return (
                    `${studentName}: assessment data ` +
                    `is missing.`
                );
            }

            // Overall assessment is optional: a teacher may leave
            // the final grade blank until later in the term.

            // Find DOK levels that contain achieved Skills
            const achievedDOKLevels = new Set();

            skills.forEach((skill) => {
                const isAchieved =
                    student.entries?.[skill.id] === true;

                if (isAchieved) {
                    achievedDOKLevels.add(
                        skill.dok_level.level
                    );
                }
            });

            for (const dokLevel of achievedDOKLevels) {
                const proficiency =
                    student.dokProficiency?.[
                        dokLevel
                    ];

                if (!proficiency) {
                    return (
                        `${studentName} — DOK ${dokLevel}: ` +
                        `a proficiency is required because ` +
                        `one or more Skills were selected.`
                    );
                }
            }

            // Reverse case: a proficiency was picked for a DOK
            // level, but no Skill in that level is checked. A
            // proficiency without an achieved Skill would show
            // up meaningless on the report, so it is blocked the
            // same as the missing-proficiency case above.
            for (const dok of dokLevels) {
                const proficiency =
                    student.dokProficiency?.[
                        dok.level
                    ];

                if (
                    proficiency &&
                    !achievedDOKLevels.has(
                        dok.level
                    )
                ) {
                    return (
                        `${studentName} — DOK ${dok.level}: ` +
                        `${dok.description}. Proficiency ` +
                        `selected but no Skill applied. ` +
                        `Skills required.`
                    );
                }
            }

            return null;
        },
        [
            skills,
            dokLevels,
            students,
        ]
    );

    // =========================================================
    // Build one student's API payload
    // =========================================================

    const buildStudentPayload = useCallback(
        (student) => {
            return {
                student: student.student,

                overall_assessment:
                    student.overallAssessment,

                // assessment_status is computed server-side
                // from the Skill entries below — it is not
                // sent from here.

                educator_comment:
                    student.educatorComment || "",

                // IMPORTANT:
                // Send every current Skill.
                entries: skills.map((skill) => ({
                    skill: skill.id,
                    is_achieved:
                        student.entries?.[
                            skill.id
                        ] === true,
                })),

                // Send all four DOK rows.
                dok_assessments: dokLevels.map(
                    (dok) => ({
                        dok_level: dok.level,
                        proficiency:
                            student.dokProficiency?.[
                                dok.level
                            ] || "",
                    })
                ),
            };
        },
        [
            skills,
            dokLevels,
        ]
    );

    // =========================================================
    // Save selected students
    // =========================================================

    const saveStudents = useCallback(
        async (studentIds = []) => {
            setSaveError(null);

            if (
                !subjectId ||
                !unitId ||
                !termNumber
            ) {
                const message =
                    "Subject, Unit and Term are required.";

                setSaveError(message);

                return {
                    success: false,
                    error: message,
                };
            }

            if (!studentIds.length) {
                const message =
                    "At least one student must be selected.";

                setSaveError(message);

                return {
                    success: false,
                    error: message,
                };
            }

            const studentsToSave = [];

            for (const studentId of studentIds) {
                const student =
                    studentState[studentId];

                const validationError =
                    validateStudent(
                        studentId,
                        student
                    );

                if (validationError) {
                    setSaveError(
                        validationError
                    );

                    return {
                        success: false,
                        error: validationError,
                        studentId,
                    };
                }

                studentsToSave.push(
                    buildStudentPayload(student)
                );
            }

            const payload = {
                subject: subjectId,
                unit: unitId,
                term_number: Number(termNumber),
                students: studentsToSave,
            };

            setIsSaving(true);

            try {
                const response = await api.post(
                    `${baseUrl}/grades/progress-card/batch/save/`,
                    payload,
                    {
                        withCredentials: true,
                    }
                );

                if (onSaveSuccess) {
                    await onSaveSuccess(
                        response.data
                    );
                }

                return {
                    success: true,
                    data: response.data,
                };
            } catch (err) {
                console.error(
                    "Failed to save batch assessment:",
                    err
                );

                const message =
                    err.response?.data?.error ||
                    "Unable to save batch assessment.";

                setSaveError(message);

                return {
                    success: false,
                    error: message,
                };
            } finally {
                setIsSaving(false);
            }
        },
        [
            baseUrl,
            subjectId,
            unitId,
            termNumber,
            studentState,
            validateStudent,
            buildStudentPayload,
            onSaveSuccess,
        ]
    );

    return {
        saveStudents,
        isSaving,
        saveError,
        clearSaveError: () =>
            setSaveError(null),
    };
};

export default useBatchAssessmentSave;